import API_BASE from "./apiConfig";
import React, { useEffect, useState, useRef } from "react";
import {
  FaSearch, FaSave, FaInfoCircle, FaUser,
  FaCommentDots, FaChartLine, FaCheckCircle, FaExclamationCircle,
  FaTimes, FaTransgender, FaBirthdayCake, FaIdCard,
  FaGraduationCap, FaBaseballBall, FaShieldAlt,
} from "react-icons/fa";
import { Colors } from "./branding";
import {useTheme, getT, getBrandColors, darken} from "./ThemeContext";
import PageHeaderIcon from "./components/PageHeaderIcon";
import { useAgeCategories, categoryForAge } from "./hooks/useLookups";
import { useDateTimeFormat } from "./utils/formatters";
const BASE = API_BASE;

// ─── Shared style functions (accept T token object) ───────────────────────────
const labelSt = (T) => ({
  display: "block",
  marginBottom: "7px",
  fontSize: "11px",
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: T.textLabel,
  fontFamily: T.font,
});

const inputWrapSt = (T) => ({
  display: "flex",
  alignItems: "center",
  border: `1.5px solid ${T.inputBdr}`,
  borderRadius: T.inputRadius || "10px",
  padding: "10px 14px",
  background: T.inputBg,
  backdropFilter: T.blur, WebkitBackdropFilter: T.blur,
  WebkitBackdropFilter: T.blur,
  width: "100%",
  boxSizing: "border-box",
});

const inputSt = (T) => ({
  border: "none",
  outline: "none",
  width: "100%",
  background: "transparent",
  fontSize: "14px",
  color: T.inputText,
  fontFamily: T.font,
});

const glassCard = (T) => ({
  background: T.glass,
  backdropFilter: T.blur, WebkitBackdropFilter: T.blur,
  WebkitBackdropFilter: T.blur,
  border: T.glassBdr,
  borderRadius: T.cardRadius || "16px",
  boxShadow: T.glassSh,
  padding: "24px 28px",
  boxSizing: "border-box",
});

const yellowBtn = {
  padding: "12px 28px",
  border: "none",
  borderRadius: "10px",
  fontSize: "14px",
  fontWeight: 800,
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
  get color() { return getBrandColors().blueDark; },
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  backgroundImage: `linear-gradient(135deg, ${getBrandColors().yellow}, ${darken(getBrandColors().yellow)})`,
  boxShadow: "0 4px 20px rgba(247,206,11,0.35)",
};

const toastSt = (ok) => ({
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "13px 18px",
  borderRadius: "10px",
  marginBottom: "20px",
  fontSize: "14px",
  fontWeight: 600,
  background: ok
    ? "rgba(39,174,96,0.15)"
    : "rgba(220,38,38,0.15)",
  color: ok ? "#4ade80" : "#f87171",
  border: ok
    ? "1px solid rgba(74,222,128,0.30)"
    : "1px solid rgba(248,113,113,0.30)",
  backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  boxShadow: "0 4px 28px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.06)",
});

// ─── Age category helpers ─────────────────────────────────────────────────────
const AGE_CAT_COLORS = {
  U9:    { bg: "rgba(251,191,36,0.15)",  border: "#fbbf24", text: "#fcd34d" },
  U11:   { bg: "rgba(52,211,153,0.15)",  border: "#34d399", text: "#6ee7b7" },
  U13:   { bg: "rgba(96,165,250,0.15)",  border: "#60a5fa", text: "#93c5fd" },
  U15:   { bg: "rgba(167,139,250,0.15)", border: "#a78bfa", text: "#c4b5fd" },
  U17:   { bg: "rgba(244,114,182,0.15)", border: "#f472b6", text: "#f9a8d4" },
  Adult: { bg: "rgba(148,163,184,0.15)", border: "#94a3b8", text: "#cbd5e1" },
};

function computeAgeCategory(dobStr, categories) {
  if (!dobStr) return null;
  const dob   = new Date(dobStr + "T00:00:00");
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  if (
    today.getMonth() < dob.getMonth() ||
    (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())
  ) age--;
  return categoryForAge(age, categories);
}

function AgeCategoryBadge({ code }) {
  if (!code) return null;
  const c = AGE_CAT_COLORS[code] || AGE_CAT_COLORS["Adult"];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 800,
      background: c.bg, border: `1.5px solid ${c.border}`, color: c.text,
      letterSpacing: "0.03em", whiteSpace: "nowrap",
    }}>
      <FaBirthdayCake size={10} /> {code}
    </span>
  );
}

// ─── Rich student info header card ────────────────────────────────────────────
function StudentInfoCard({ student }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const [imgErr, setImgErr] = useState(false);
  const ageCategories = useAgeCategories();
  const { fmtDate } = useDateTimeFormat();
  if (!student) return null;
  const ageCode     = computeAgeCategory(student.date_of_birth, ageCategories);
  const statusLabel = student.active_status === 1 ? "Active"
                    : student.active_status === 2 ? "Suspended" : "Cancelled";
  const statusColor = student.active_status === 1 ? "#4ade80"
                    : student.active_status === 2 ? "#fbbf24" : "#f87171";
  const statusBg    = student.active_status === 1 ? "rgba(74,222,128,0.12)"
                    : student.active_status === 2 ? "rgba(251,191,36,0.12)" : "rgba(248,113,113,0.12)";
  const initials = `${student.first_name?.charAt(0) || ""}${student.last_name?.charAt(0) || ""}`;

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap", width: "100%",
    }}>
      {/* Avatar */}
      <div style={{
        width: 84, height: 84, borderRadius: "50%", flexShrink: 0,
        overflow: "hidden",
        background: `linear-gradient(135deg, rgba(247,206,11,0.20), rgba(3,78,123,0.20))`,
        border: `3px solid rgba(247,206,11,0.40)`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 28, fontWeight: 800, color: getBrandColors().yellow,
        boxShadow: "0 6px 24px rgba(0,0,0,0.40), 0 0 0 1px rgba(255,255,255,0.06)",
      }}>
        {student.photo_url && !imgErr
          ? <img src={`${BASE}/${student.photo_url}`} alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={() => setImgErr(true)} />
          : <span>{initials}</span>
        }
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Row 1 — name + badges */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
          <span style={{ fontSize: 21, fontWeight: 800, color: T.textPrimary, letterSpacing: "-0.01em" }}>
            {student.first_name} {student.last_name}
          </span>
          {ageCode && <AgeCategoryBadge code={ageCode} />}
          <span style={{
            display: "inline-flex", alignItems: "center",
            padding: "3px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700,
            background: statusBg, color: statusColor,
            border: `1.5px solid ${statusColor}44`,
          }}>{statusLabel}</span>
        </div>
        {/* Row 2 — meta chips */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "5px 18px", fontSize: 13, color: T.textMuted }}>
          {student.student_code && (
            <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <FaIdCard size={12} color={T.textMuted} />
              ID:&nbsp;<strong style={{ color: T.inputText }}>{student.student_code}</strong>
            </span>
          )}
          {student.gender && (
            <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <FaTransgender size={12} color={T.textMuted} /> {student.gender}
            </span>
          )}
          {student.date_of_birth && (
            <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <FaBirthdayCake size={12} color={T.textMuted} />
              {fmtDate(student.date_of_birth)}
            </span>
          )}
          {student.level_name && (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 700,
              color: student.color_hex || getBrandColors().yellow,
              background: (student.color_hex || getBrandColors().yellow) + "22",
              border: `1px solid ${(student.color_hex || getBrandColors().yellow)}44`,
            }}>
              <FaGraduationCap size={10} /> {student.level_name}
            </span>
          )}
          {student.role_name && (
            <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <FaBaseballBall size={11} color={T.textMuted} /> {student.role_name}
            </span>
          )}
          {student.cricclub_player_id && (
            <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <FaShieldAlt size={11} color={T.textMuted} /> CricClub:&nbsp;
              <strong style={{ color: T.inputText }}>{student.cricclub_player_id}</strong>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", margin: "6px 0 14px" }}>
      <div style={{ height: "2px", width: "18px", background: getBrandColors().yellow, borderRadius: "2px" }} />
      <span style={{ fontSize: "11px", fontWeight: 800, letterSpacing: "0.10em", textTransform: "uppercase", color: T.textMuted }}>
        {children}
      </span>
      <div style={{ flex: 1, height: "1px", background: T.divider }} />
    </div>
  );
}

// ─── Info Popup ───────────────────────────────────────────────────────────────
function InfoPopup({ title, text, onClose }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(0,0,0,0.70)",
      backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
      display: "flex", justifyContent: "center", alignItems: "center",
      zIndex: 9999,
    }}>
      <div style={{
        width: "480px", maxHeight: "70vh",
        padding: "28px 32px",
        borderRadius: "16px",
        background: T.glass,
        backdropFilter: T.blur, WebkitBackdropFilter: T.blur,
        WebkitBackdropFilter: T.blur,
        border: T.glassBdr,
        boxShadow: T.glassSh,
        overflowY: "auto", WebkitOverflowScrolling: "touch",
        position: "relative",
      }}>
        <button onClick={onClose} style={{
          position: "absolute", top: "14px", right: "16px",
          background: T.btnGlassBg, border: T.btnGlassBdr,
          borderRadius: "50%", width: "28px", height: "28px",
          cursor: "pointer", WebkitTapHighlightColor: "transparent",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: T.textLabel,
        }}>
          <FaTimes size={12} />
        </button>
        <h3 style={{ marginTop: 0, color: getBrandColors().yellow, fontSize: "16px", fontWeight: 800, marginBottom: "12px", textShadow: "0 0 16px rgba(247,206,11,0.45)" }}>
          {title}
        </h3>
        <p style={{ fontSize: "14px", color: T.textSub, lineHeight: "1.6", whiteSpace: "pre-wrap", margin: 0 }}>
          {text || "No description available."}
        </p>
      </div>
    </div>
  );
}

// ─── Score selector pill ──────────────────────────────────────────────────────
function ScoreSelect({ value, onChange }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const scores = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  return (
    <select
      value={value || ""}
      onChange={onChange}
      style={{
        width: "100%",
        padding: "6px 8px",
        borderRadius: "8px",
        border: `1.5px solid ${value
          ? Number(value) >= 8 ? "rgba(74,222,128,0.50)"
          : Number(value) >= 5 ? "rgba(251,191,36,0.50)"
          : "rgba(248,113,113,0.50)"
          : T.inputBdr}`,
        background: T.inputBg,
        fontSize: "13px",
        color: value ? T.inputText : T.textMuted,
        fontWeight: value ? 700 : 400,
        cursor: "pointer", WebkitTapHighlightColor: "transparent",
        outline: "none",
        appearance: "auto",
        colorScheme: isDark ? "dark" : "light",
      }}
    >
      <option value="">—</option>
      {scores.map((n) => (
        <option key={n} value={n}>{n}</option>
      ))}
    </select>
  );
}

// ─── Score badge colour ───────────────────────────────────────────────────────
const scoreBg = (v) => {
  if (!v) return "rgba(255,255,255,0.04)";
  const n = Number(v);
  if (n >= 8) return "rgba(74,222,128,0.08)";
  if (n >= 5) return "rgba(251,191,36,0.08)";
  return "rgba(248,113,113,0.08)";
};

// ─── Main component ───────────────────────────────────────────────────────────
const StudentPerformanceForm = ({ currentUserId }) => {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const [searchText, setSearchText]         = useState("");
  const [searchResults, setSearchResults]   = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [categories, setCategories]         = useState([]);
  const [scores, setScores]                 = useState({});
  const [coachComments, setCoachComments]   = useState("");
  const [msg, setMsg]                       = useState(null); // {ok, text}
  const [popup, setPopup]                   = useState(null); // {title, text}
  const topRef = useRef(null);

  // Load KPI structure once
  useEffect(() => {
    fetch(`${BASE}/performance/structure`)
      .then((r) => r.json())
      .then(setCategories)
      .catch(() => setCategories([]));
  }, []);

  // Debounced search
  useEffect(() => {
    if (searchText.trim().length < 2) { setSearchResults([]); return; }
    const t = setTimeout(() => {
      fetch(`${BASE}/students/search?name=${encodeURIComponent(searchText)}`)
        .then((r) => r.json())
        .then((d) => setSearchResults(Array.isArray(d.students) ? d.students : []))
        .catch(() => setSearchResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [searchText]);

  const handleSelectStudent = async (student) => {
    setSearchText("");
    setSearchResults([]);
    const info = await fetch(`${BASE}/performance/student-info/${student.student_id}`)
      .then((r) => r.json());
    setSelectedStudent(info);
    fetch(`${BASE}/performance/student/${student.student_id}`)
      .then((r) => r.json())
      .then((data) => {
        const existing = {};
        data.forEach((item) => { existing[item.indicator_id] = item.score; });
        setScores(existing);
      });
  };

  const handleSubmit = async () => {
    if (!selectedStudent) { setMsg({ ok: false, text: "Please select a student first." }); return; }
    const payload = {
      student_id: selectedStudent.student_id,
      scores: Object.entries(scores).map(([id, score]) => ({
        indicator_id: Number(id),
        score: Number(score),
      })),
      coach_comments: coachComments,
      login_id: currentUserId || "CoachLogin",
    };
    try {
      const res = await fetch(`${BASE}/performance/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      setMsg({ ok: true, text: result.message || "Performance saved successfully!" });
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch {
      setMsg({ ok: false, text: "Network error. Please try again." });
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div
      ref={topRef}
      style={{
        minHeight: "100dvh",
        background: T.pageBg,
        padding: "20px 28px 48px",
        boxSizing: "border-box",
        fontFamily: T.font,
        overflowX: "hidden",
      }}
    >
      {/* Info Popup */}
      {popup && <InfoPopup title={popup.title} text={popup.text} onClose={() => setPopup(null)} />}

      {/* ── Page Header ── */}
      <div style={{ display: "flex", alignItems: "center", gap: "14px", marginBottom: "20px" }}>
        <PageHeaderIcon icon={<FaChartLine size={18} color={getBrandColors().blueDark} />} />
        <div>
          <h2 style={{ fontSize: "22px", fontWeight: 900, color: T.textPrimary, margin: 0, letterSpacing: "-0.01em" }}>
            Student Performance
          </h2>
          <p style={{ margin: 0, fontSize: "12px", color: T.textMuted, marginTop: "1px" }}>
            Score KPIs and save coach feedback per student
          </p>
        </div>
      </div>

      {/* ── Top row: Search LEFT, Student card RIGHT ── */}
      <div style={{
        display: "flex",
        alignItems: "stretch",
        gap: "16px",
        marginBottom: "16px",
        width: "100%",
        flexWrap: "wrap",
      }}>
        {/* Search card */}
        <div style={{ ...glassCard(T), padding: "18px 20px", flex: "0 0 280px", minWidth: 220, position: "relative", zIndex: 100 }}>
          <SectionLabel>Select Student</SectionLabel>
          <label style={labelSt(T)}>Search by name</label>
          <div style={{ position: "relative" }}>
            <div style={inputWrapSt(T)}>
              <FaSearch style={{ marginRight: "10px", color: T.textMuted, flexShrink: 0 }} />
              <input
                style={inputSt(T)}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Type first or last name…"
              />
              {searchText && (
                <button
                  onClick={() => { setSearchText(""); setSearchResults([]); setSelectedStudent(null); }}
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    WebkitTapHighlightColor: "transparent",
                    color: T.textMuted, padding: "0 0 0 6px", flexShrink: 0,
                  }}
                >
                  <FaTimes size={12} />
                </button>
              )}
            </div>
            {searchResults.length > 0 && (
              <div style={{
                position: "absolute", top: "calc(100% + 6px)", left: 0, width: "100%",
                background: T.dropdownBg,
                backdropFilter: T.blur, WebkitBackdropFilter: T.blur,
                border: T.dropdownBdr,
                borderRadius: "12px",
                boxShadow: T.glassSh,
                zIndex: 1000,
                maxHeight: "200px", overflowY: "auto", WebkitOverflowScrolling: "touch",
              }}>
                {searchResults.map((s, i) => (
                  <div
                    key={s.student_id}
                    onClick={() => handleSelectStudent(s)}
                    style={{
                      padding: "11px 16px", cursor: "pointer", WebkitTapHighlightColor: "transparent",
                      fontSize: "14px",
                      borderBottom: i < searchResults.length - 1 ? `1px solid ${T.divider}` : "none",
                      color: T.dropdownItem, fontWeight: 500,
                      display: "flex", alignItems: "center", gap: "8px",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(247,206,11,0.12)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    <FaUser size={11} style={{ color: T.textMuted }} />
                    {s.first_name} {s.last_name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Student info card */}
        {selectedStudent && (
          <div style={{
            ...glassCard(T),
            padding: "18px 24px",
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
          }}>
            <StudentInfoCard student={selectedStudent} />
          </div>
        )}
      </div>

      {/* Toast */}
      {msg && (
        <div style={{ ...toastSt(msg.ok), maxWidth: "860px", margin: "0 auto 20px" }}>
          {msg.ok ? <FaCheckCircle /> : <FaExclamationCircle />} {msg.text}
        </div>
      )}

      {/* ── KPI Section ── */}
      <div style={{ maxWidth: "100%", margin: "0 auto" }}>

        {categories.map((cat) => (
          <div key={cat.category_id} style={{ marginBottom: "20px" }}>

            {/* Category header */}
            <div style={{
              display: "flex", alignItems: "center", gap: "14px",
              padding: "13px 20px", borderRadius: "14px",
              background: `linear-gradient(110deg, rgba(2,48,79,0.90) 0%, rgba(3,62,98,0.85) 55%, rgba(2,50,80,0.90) 100%)`,
              backdropFilter: T.blur, WebkitBackdropFilter: T.blur,
              border: T.glassBdr,
              boxShadow: T.glassSh,
              marginBottom: "12px",
              position: "relative",
              zIndex: 1,
              overflow: "hidden",
            }}>
              {/* Yellow left accent bar */}
              <div style={{
                position: "absolute", left: 0, top: 0, bottom: 0,
                width: "4px",
                background: `linear-gradient(180deg, ${getBrandColors().yellow}, ${darken(getBrandColors().yellow)})`,
                borderRadius: "14px 0 0 14px",
              }} />
              {/* Icon tile */}
              <div style={{
                width: "34px", height: "34px", borderRadius: "9px",
                background: `linear-gradient(135deg, ${getBrandColors().yellow}, ${darken(getBrandColors().yellow)})`,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
                boxShadow: "0 3px 12px rgba(247,206,11,0.40)",
                marginLeft: "6px",
              }}>
                <FaChartLine size={15} color={getBrandColors().blueDark} />
              </div>
              {/* Category name */}
              <span style={{
                fontWeight: 800, color: getBrandColors().yellow, fontSize: "15px", flex: 1,
                letterSpacing: "0.03em",
                fontFamily: T.font,
                textShadow: "0 0 18px rgba(247,206,11,0.45), 0 1px 3px rgba(0,0,0,0.30)",
              }}>
                {cat.category_name}
              </span>
              {/* Indicator count badge */}
              <div style={{
                padding: "3px 10px", borderRadius: "20px",
                background: T.btnGlassBg,
                border: T.btnGlassBdr,
                fontSize: "11px", fontWeight: 700, color: T.textSub,
                letterSpacing: "0.04em",
                fontFamily: T.font,
              }}>
                {cat.indicators.length} KPI{cat.indicators.length !== 1 ? "s" : ""}
              </div>
              {/* Info button */}
              <button
                onClick={() => setPopup({ title: cat.category_name, text: cat.description })}
                style={{
                  background: T.btnGlassBg,
                  border: T.btnGlassBdr,
                  borderRadius: "8px", padding: "6px 12px",
                  cursor: "pointer", WebkitTapHighlightColor: "transparent",
                  color: T.btnGlassClr, fontSize: "12px", fontWeight: 700,
                  display: "flex", alignItems: "center", gap: "6px",
                  fontFamily: T.font,
                  letterSpacing: "0.03em",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.16)" : "rgba(3,78,123,0.12)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = T.btnGlassBg)}
              >
                <FaInfoCircle size={16} /> Info
              </button>
            </div>

            {/* Indicator grid */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
              gap: "10px",
            }}>
              {cat.indicators.map((ind) => (
                <div key={ind.indicator_id} style={{
                  background: scoreBg(scores[ind.indicator_id]),
                  border: scores[ind.indicator_id]
                    ? `1.5px solid ${Number(scores[ind.indicator_id]) >= 8 ? "rgba(74,222,128,0.30)" : Number(scores[ind.indicator_id]) >= 5 ? "rgba(251,191,36,0.35)" : "rgba(248,113,113,0.30)"}`
                    : `1px solid ${T.inputBdr}`,
                  borderRadius: "12px",
                  padding: "12px 14px",
                  backdropFilter: T.blur, WebkitBackdropFilter: T.blur,
                  boxShadow: "0 2px 12px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.04)",
                  transition: "border 0.2s, background 0.2s",
                }}>
                  <div style={{
                    fontSize: "12px", fontWeight: 700, color: T.textSub,
                    marginBottom: "10px",
                    display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "6px",
                  }}>
                    <span style={{ lineHeight: "1.3" }}>{ind.indicator_name}</span>
                    <button
                      onClick={() => setPopup({ title: ind.indicator_name, text: ind.description })}
                      style={{
                        background: "none", border: "none", cursor: "pointer",
                        WebkitTapHighlightColor: "transparent",
                        color: T.textMuted, padding: 0, flexShrink: 0,
                        transition: "color 0.15s",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = getBrandColors().yellow)}
                      onMouseLeave={(e) => (e.currentTarget.style.color = T.textMuted)}
                    >
                      <FaInfoCircle size={16} />
                    </button>
                  </div>
                  <ScoreSelect
                    value={scores[ind.indicator_id]}
                    onChange={(e) => setScores((p) => ({ ...p, [ind.indicator_id]: e.target.value }))}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Coach Comments */}
        <div style={{ ...glassCard(T), marginTop: "8px", marginBottom: "20px" }}>
          <SectionLabel>Coach Comments</SectionLabel>
          <label style={labelSt(T)}>
            <FaCommentDots style={{ marginRight: "6px" }} />
            Feedback &amp; Notes
          </label>
          <textarea
            style={{
              width: "100%",
              minHeight: "90px",
              padding: "12px 14px",
              borderRadius: T.inputRadius || "10px",
              border: `1.5px solid ${T.inputBdr}`,
              background: T.inputBg,
              backdropFilter: T.blur, WebkitBackdropFilter: T.blur,
              WebkitBackdropFilter: T.blur,
              boxShadow: "inset 0 2px 8px rgba(0,0,0,0.20)",
              fontSize: "14px",
              color: T.inputText,
              resize: "vertical",
              outline: "none",
              boxSizing: "border-box",
              fontFamily: T.font,
              colorScheme: isDark ? "dark" : "light",
            }}
            value={coachComments}
            onChange={(e) => setCoachComments(e.target.value)}
            placeholder="Enter detailed feedback for this student…"
          />
        </div>

        {/* Save button */}
        <button onClick={handleSubmit} style={{ ...yellowBtn, minWidth: "220px" }}>
          <FaSave size={16} /> Save Performance
        </button>

      </div>
    </div>
  );
};

export default StudentPerformanceForm;
